import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { useToast } from '@/components/ui/use-toast'
import { isAxiosError } from '@/api/business'
import Button from '@/components/Button'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { useEffect } from 'react'
import { Resale } from '@/types/Resale'
import { useUpdateResaleOwnerMutation } from '@/queries/useResaleQuery'

interface EditOwnerDialogProps {
    resale: Resale | null
    isOpen: boolean
    onClose: () => void
}

const schema = z.object({
    DS_PROPRIETARIO: z.string().max(150, {
        message: 'O nome do proprietário deve ter no máximo 150 caracteres',
    }).optional().nullable(),
})

const EditOwnerDialog = ({ resale, isOpen, onClose }: EditOwnerDialogProps) => {
    const { toast } = useToast()
    const { mutate, isPending } = useUpdateResaleOwnerMutation()

    const form = useForm<z.infer<typeof schema>>({
        resolver: zodResolver(schema),
        defaultValues: {
            DS_PROPRIETARIO: resale?.DS_PROPRIETARIO || '',
        },
    })

    useEffect(() => {
        if (resale) {
            form.reset({
                DS_PROPRIETARIO: resale.DS_PROPRIETARIO || '',
            })
        }
    }, [resale, form])

    const onSubmit = (values: z.infer<typeof schema>) => {
        if (!resale) return

        mutate(
            {
                SK_REVENDA: resale.SK_REVENDA,
                DS_PROPRIETARIO: values.DS_PROPRIETARIO || null,
            },
            {
                onSuccess: () => {
                    toast({
                        title: 'Proprietário atualizado com sucesso!',
                    })
                    onClose()
                },
                onError: (err) => {
                    const message = isAxiosError(err)
                        ? err.response?.data?.message || 'Erro ao atualizar proprietário'
                        : 'Erro ao atualizar proprietário'
                    toast({
                        variant: 'destructive',
                        title: 'Erro',
                        description: message,
                    })
                },
            }
        )
    }

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>Editar Proprietário - {resale?.DS_REVENDA}</DialogTitle>
                </DialogHeader>

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                        <FormField
                            control={form.control}
                            name="DS_PROPRIETARIO"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Nome do Proprietário</FormLabel>
                                    <FormControl>
                                        <Input
                                            placeholder="Digite o nome do proprietário..."
                                            {...field}
                                            value={field.value || ''}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <DialogFooter className="pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={onClose}
                                disabled={isPending}
                            >
                                Cancelar
                            </Button>
                            <Button type="submit" isLoading={isPending}>
                                Salvar
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    )
}

export default EditOwnerDialog
